import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import icon from "../../assets/images/brand/logo_2_107x70.png";
import { PasswordInput } from "../../components/CommonComponents/Input/Input";
import { isUserOrArchitect } from "../../hoc/isUserOrArchitect";
import { useChangePassword } from "../../hooks/user.hooks";
import styles from "../forgotPassword/forgotPassword.module.css";
import { ErrorToast } from "../../components/toastMessage";

const checkPasswordStrength = (password: string) => {
  if (!password) return '--';
  // const strongRegex = new RegExp(
  //   `^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])` +
  //   `(?=.*[!@#$%^&*])` +
  //   `(?!.*(.)\\1{2})` +
  //   `(?!.*(123|234|345|456|567|678|789|987|876|765|654|543|432|321|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz))` +
  //   `(?=.{8,})`
  // );
  // const mediumRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])(?!.*\s).{7,}$/;
  const strongRegex = new RegExp(
    `^(?=.*[a-z])` +                     // At least one lowercase
    `(?=.*[A-Z])` +                     // At least one uppercase
    `(?=.*\\d)` +                       // At least one digit
    `(?=.*[!@#$%^&*])` +                // At least one special character
    `(?!.*(.)\\1{2})` +                 // No character repeats 3+ times consecutively
    `(?!.*(123|234|345|456|567|678|789|890|012|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz))` + // No sequential patterns
    `.{8,}$`                            // Minimum 8 characters
  );

  const mediumRegex = new RegExp(
    `^(?=.*[a-z])` +
    `(?=.*[A-Z])` +
    `(?=.*\\d)` +
    `(?=.*[@$!%*?&])` +
    `(?!.*(.)\\1{2})` +
    `.{7,}$`
  );

  if (strongRegex.test(password)) return 'Strong';
  if (mediumRegex.test(password)) return 'Medium';
  return 'Weak';
};

const getPasswordStrengthColor = (strength: string) => {
  switch (strength) {
    case 'Strong':
      return 'text-green-500';
    case 'Medium':
      return 'text-yellow-500';
    case 'Weak':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
};

export const ChangePassword = isUserOrArchitect(() => {
  const navigate = useNavigate();
  const changePasswordMutation = useChangePassword();
  const [newPasswordStrength, setNewPasswordStrength] = useState('--');
  const [confirmPasswordStrength, setConfirmPasswordStrength] = useState('--');
  const { formState, getValues, register, handleSubmit, watch } = useForm({
    defaultValues: { oldPassword: "", newPassword: "", confirmPassword: "" },
  });

  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    setNewPasswordStrength(checkPasswordStrength(newPassword));
  }, [newPassword]);

  useEffect(() => {
    setConfirmPasswordStrength(checkPasswordStrength(confirmPassword));
  }, [confirmPassword]);

  const validatePassword = (value: any) => {
    if (checkPasswordStrength(value) !== 'Strong') {
      ErrorToast("Please enter a password with a minimum of 8 characters and ensure it is not sequential (e.g. 123/abc).");
      return "Password must be strong";
    }
    return true;
  };


  return (
    <div className={styles.loginContainer}>
      <form
        noValidate
        onSubmit={handleSubmit((data) => {
          changePasswordMutation.mutate(data, {
            onSuccess: () => navigate(-1),
          });
        })}
        className="p-8 bg-[rgba(255,_255,_255,_.9)] rounded-3xl min-w-[500px]"
      >
        <div className="flex justify-center mb-4">
          <img src={icon} alt="Logo" />
        </div>
        <h4 className="text-xl font-semibold text-center mb-8">
          Change Password
        </h4>
        <PasswordInput
          placeholder="Old Password"
          label="Old Password"
          containerClass="mb-4"
          error={!!formState?.errors?.oldPassword}
          helperText={formState?.errors?.oldPassword?.message?.toString()}
          {...register("oldPassword", { required: "* Required" })}
        />
        <PasswordInput
          placeholder="New Password"
          label="New Password"
          containerClass="mb-4"
          error={!!formState?.errors?.newPassword}
          helperText={formState?.errors?.newPassword?.message?.toString()}
          {...register("newPassword", {
            required: "* Required",
            minLength: {
              value: 8,
              message: "Please enter at least 8 characters",
            },
            validate: validatePassword
          })}
        />

        <div className="mb-4 flex justify-center items-center">
          <span>Password strength: </span>
          <span className={`font-semibold ${getPasswordStrengthColor(newPasswordStrength)}`}>
            {newPasswordStrength}
          </span>
        </div>

        <PasswordInput
          placeholder="Confirm Password"
          label="Confirm Password"
          containerClass="mb-4"
          error={!!formState?.errors?.confirmPassword}
          helperText={formState?.errors?.confirmPassword?.message?.toString()}
          {...register("confirmPassword", {
            required: "* Required",
            validate: (v) =>
              v === getValues("newPassword") ? true : "Passwords don't match",
          })}
        />

        <div className="mb-4 flex justify-center items-center">
          <span>Password strength: </span>
          <span className={`font-semibold ${getPasswordStrengthColor(confirmPasswordStrength)}`}>
            {confirmPasswordStrength}
          </span>
        </div>
        <button
          type="submit"
          disabled={changePasswordMutation.isPending}
          className="bg-primary px-6 py-2 rounded-full font-bold text-white mx-auto block"
        >
          {changePasswordMutation.isPending ? "Updating" : "Update"}
        </button>

        <div className="max-w-[465px] flex justify-center items-center text-center mt-2 mb-2 text-sm text-gray-600">
          Password must be strong: Minimum 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
        </div>
      </form>
    </div>
  );
});